import type { ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { AuthProvider, useAuth } from "./components/Auth";
import { FeedbackProvider } from "./components/Feedback";
import { Layout } from "./components/Layout";
import { Shell } from "./components/next/Shell";
import { Loading, PageHeader } from "./components/Page";
import { CustomerDetail, Customers } from "./pages/Customers";
import { DataQuality } from "./pages/DataQuality";
import { Depletions } from "./pages/Depletions";
import { DocumentDetail } from "./pages/DocumentDetail";
import { DocumentForm } from "./pages/DocumentForm";
import { Documents } from "./pages/Documents";
import { LocationDetail, Locations } from "./pages/Locations";
import { ForcePasswordChange, Login } from "./pages/Login";
import { Overview } from "./pages/Overview";
import { Home } from "./pages/next/Home";
import { nextPages } from "./pages/next/registry";
import { Production } from "./pages/Production";
import { Costs } from "./pages/Costs";
import { ProductDetail, Products } from "./pages/Products";
import { QuickBooks } from "./pages/QuickBooks";
import { InvoiceDetail, Sales } from "./pages/Sales";
import { PartyForm, Settings } from "./pages/Settings";
import { RunDetail, SourceDetail, Sources } from "./pages/Sources";
import { Stock } from "./pages/Stock";
import { Tasks } from "./pages/Tasks";
import { Shipments } from "./pages/Shipments";

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeedbackProvider>
          <Gate />
        </FeedbackProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

/** Nothing renders — and no data is fetched — until there is a session. */
function Gate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="signin"><Loading label="Loading…" /></div>;
  if (!user) return <Login />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return <AppRoutes />;
}

/** The same pages and URLs in both designs; the person's own choice picks the frame. */
function AppRoutes() {
  const { user } = useAuth();
  const isNew = user?.ui === "new";
  const Frame = isNew ? Shell : Layout;
  /** The redone page for this route when the person is on the new design and it exists; the classic one otherwise. */
  const pick = (path: string, classic: ReactNode) => {
    const Next = isNew ? nextPages[path] : undefined;
    return Next ? <Next /> : classic;
  };
  return (
    <Frame>
          <Routes>
            <Route path="/" element={isNew ? <Home /> : <Overview />} />
            <Route path="/stock" element={pick("/stock", <Stock />)} />
            <Route path="/production" element={pick("/production", <Production />)} />
            <Route path="/documents" element={pick("/documents", <Documents />)} />
            <Route path="/documents/new" element={pick("/documents/new", <DocumentForm />)} />
            <Route path="/documents/:id" element={pick("/documents/:id", <DocumentDetail />)} />
            <Route path="/documents/:id/edit" element={pick("/documents/:id/edit", <DocumentForm />)} />
            <Route path="/sales" element={pick("/sales", <Sales />)} />
            <Route path="/sales/invoices/:channel/:invoice" element={pick("/sales/invoices/:channel/:invoice", <InvoiceDetail />)} />
            <Route path="/depletions" element={pick("/depletions", <Depletions />)} />
            <Route path="/shipments" element={pick("/shipments", <Shipments />)} />
            <Route path="/quickbooks" element={pick("/quickbooks", <QuickBooks />)} />
            <Route path="/customers" element={pick("/customers", <Customers />)} />
            <Route path="/customers/:id" element={pick("/customers/:id", <CustomerDetail />)} />
            <Route path="/costs" element={pick("/costs", <Costs />)} />
            <Route path="/tasks" element={pick("/tasks", <Tasks />)} />
            <Route path="/products" element={pick("/products", <Products />)} />
            <Route path="/products/:sku" element={pick("/products/:sku", <ProductDetail />)} />
            <Route path="/locations" element={pick("/locations", <Locations />)} />
            <Route path="/locations/:code" element={pick("/locations/:code", <LocationDetail />)} />
            <Route path="/data-quality" element={pick("/data-quality", <DataQuality />)} />
            <Route path="/sources" element={pick("/sources", <Sources />)} />
            <Route path="/sources/:code" element={pick("/sources/:code", <SourceDetail />)} />
            <Route path="/imports/:id" element={pick("/imports/:id", <RunDetail />)} />
            <Route path="/settings" element={pick("/settings", <Settings />)} />
            <Route path="/settings/address-book/new" element={pick("/settings/address-book/new", <PartyForm />)} />
            <Route path="/settings/address-book/:id" element={pick("/settings/address-book/:id", <PartyForm />)} />
        <Route path="*" element={<PageHeader title="Page not found" sub="That address does not match anything in the app." />} />
      </Routes>
    </Frame>
  );
}
